import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Shield, AlertTriangle, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { LeagueChip, LEAGUE_COLORS } from "@/components/badges";

const LEAGUES = ["ALL", "NFL", "NBA", "NHL", "MLB"];

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [league, setLeague] = useState("ALL");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = league !== "ALL" ? { league } : {};
    const { data } = await api.get("/teams", { params });
    setTeams(data);
    setLoading(false);
  }, [league]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1400px] mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100">Teams</h1>
        <p className="text-sm text-slate-400 mt-1">Browse every team, jump to a roster, and see who's dinged up at a glance.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {LEAGUES.map((l) => (
          <button key={l} data-testid={`teams-league-${l.toLowerCase()}`} onClick={() => setLeague(l)}
            className={`px-3 py-1.5 rounded-lg text-sm font-mono font-semibold transition-colors border ${league === l ? "text-[#04140F] border-transparent" : "text-slate-300 border-[#232D42] hover:bg-[#151B2B]"}`}
            style={league === l ? { background: l === "ALL" ? "#00E5A3" : LEAGUE_COLORS[l] } : {}}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-500 font-mono text-sm py-12 text-center">Loading teams…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="teams-grid">
          {teams.map((t) => (
            <Link key={t.id} to={`/teams/${t.id}`} data-testid={`team-card-${t.id}`}
              className="bg-[#0F1420] border border-[#232D42] rounded-xl p-5 hover:border-[#00E5A3]/40 transition-all">
              <div className="flex items-center justify-between mb-3">
                <LeagueChip league={t.league} />
                <span className="font-mono text-xs text-slate-500">{t.abbr}</span>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Shield size={18} className="text-slate-500 shrink-0" />
                <h3 className="font-heading font-semibold text-slate-100 leading-tight">{t.name}</h3>
              </div>
              <div className="flex items-center gap-4 text-xs font-mono">
                <span className="text-slate-400">{t.player_count} players</span>
                {t.watch_count > 0 && <span className="flex items-center gap-1 text-[#FFB020]"><AlertTriangle size={12} /> {t.watch_count}</span>}
                {t.out_count > 0 && <span className="flex items-center gap-1 text-[#FF4D4D]"><XCircle size={12} /> {t.out_count} out</span>}
                {t.watch_count === 0 && t.out_count === 0 && <span className="text-[#00E5A3]">Full strength</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
